import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getCascadeLive } from '@/services/api/cascade-report';
import { decimal, money, num, pct } from '../format';
import { METRICS } from '../metrics';

const INTERVALO_MS = 5000;

/**
 * Faixa de tempo real, no topo da página.
 *
 * Fica fora das abas de propósito: é o número de agora, e serve de referência
 * para tudo que vem abaixo — que é consolidado e olha para trás. Enterrada numa
 * aba, obrigava a trocar de contexto para responder "e agora, como está?".
 *
 * Lê o evento cru, não a consolidação diária: é a única forma de enxergar os
 * últimos minutos. Por isso também é o único lugar onde cascatas e
 * preenchimento aparecem sempre, mesmo com a consolidação antiga incompleta.
 */
export function LiveStrip({ domainId }: { domainId: number | null }) {
    const [agora, setAgora] = useState(Date.now());

    const { data, isError, dataUpdatedAt } = useQuery({
        queryKey: ['cascade-live', domainId],
        queryFn: () => getCascadeLive(domainId),
        refetchInterval: INTERVALO_MS,
        refetchIntervalInBackground: false,
    });

    // Só para o "há N s" andar entre as buscas.
    useEffect(() => {
        const id = setInterval(() => setAgora(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    if (isError || !data) return null;

    const t = data.totals;
    // Sem a margem à vista, um número que atualiza a cada 5s passa uma
    // precisão que ele não tem.
    const margem =
        data.calibration?.calibrated && data.calibration.margin !== null
            ? `${(data.calibration.margin * 100).toFixed(0)}%`
            : null;
    const segundos = Math.max(0, Math.round((agora - dataUpdatedAt) / 1000));

    const stats = t.cascades
        ? [
              { label: METRICS.cascades.label, value: num(t.cascades) },
              { label: 'Preench.', value: t.cascades_reliable ? pct(t.fill_rate) : '—' },
              { label: 'Receita', value: money(t.revenue_expected) },
              { label: 'eCPM', value: money(t.ecpm_expected) },
              { label: 'Sol./venda', value: t.wins ? decimal(t.requests_per_sale) : '—' },
              { label: 'Viewability', value: t.wins ? pct(t.viewable_rate) : '—' },
          ]
        : [];

    return (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <span className="flex items-center gap-2 whitespace-nowrap">
                    <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    <span className="text-sm font-semibold text-gray-900">Hoje</span>
                    <span className="text-xs text-gray-500">
                        {domainId ? 'neste domínio' : 'na rede'} · há {segundos}s
                        {margem && ` · receita ± ${margem}`}
                    </span>
                </span>

                {stats.length ? (
                    stats.map((stat) => (
                        <span key={stat.label} className="flex items-baseline gap-1.5">
                            <span className="text-[11px] uppercase tracking-wide text-gray-500">
                                {stat.label}
                            </span>
                            <span className="text-sm font-semibold tabular-nums text-gray-900">
                                {stat.value}
                            </span>
                        </span>
                    ))
                ) : (
                    <span className="text-sm text-gray-500">
                        Nenhuma cascata registrada hoje ainda.
                    </span>
                )}
            </div>
        </div>
    );
}
